"use client";

import { useLayoutEffect, useState } from "react";

/**
 * The distance from the top of the document to where an element rests
 * before any scrolling, kept current as the page reflows. Handed to a sticky
 * element as its `top`, it pins exactly where it already sits, so pinning
 * is invisible: no jump up to a fixed offset, and no guess at the height of
 * whatever is above it.
 *
 * A callback ref rather than a ref object: the element mounts only once the
 * page's data is in, after the first render, and an effect keyed on a ref
 * object would have measured nothing and never looked again.
 *
 * Measured with the element unpinned, so the reading is the resting
 * position even if the page is already scrolled when it mounts.
 */
export function useRestingTop<T extends HTMLElement>(): {
  ref: (node: T | null) => void;
  top: number | undefined;
} {
  const [node, setNode] = useState<T | null>(null);
  const [top, setTop] = useState<number | undefined>(undefined);

  useLayoutEffect(() => {
    if (!node) return;
    const measure = () => {
      const previous = node.style.position;
      node.style.position = "static";
      const resting = node.getBoundingClientRect().top + window.scrollY;
      node.style.position = previous;
      setTop(Math.round(resting));
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    // The header above is what moves the resting position; it is a sibling
    // of the grid, so watch the whole column rather than only the aside.
    const column = node.parentElement?.parentElement;
    if (column) observer.observe(column);
    window.addEventListener("resize", measure);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [node]);

  return { ref: setNode, top };
}
